import React, { useState } from 'react';
import { isEarlyGame, isNoCoverage, saveLineToCache, getLineFromCache, getDisplayMinJugados, periodLabel, esHandballPick } from '../utils';
import LineStatusBadge from './LineStatusBadge';
import Tooltip from './Tooltip';

const WAIT_STATES = ['no bet','no under','no over','jugable','wait','esperar','salir','sin línea','sin linea'];
function isWaitDecision(d) { return WAIT_STATES.some(w => d.includes(w)); }

function getVerdictClass(dec) {
  const d = (dec || '').toLowerCase();
  if (isWaitDecision(d)) return 'wait';
  if (d.includes('early') || d.includes('temprano')) return 'early';
  if (d.includes('late')) return 'late';
  if (d.includes('fuerte')) return d.includes('under') ? 'fuerte-under' : 'fuerte-over';
  if (d.includes('under')) return 'under';
  if (d.includes('over')) return 'over';
  return 'wait';
}
function getVerdictLabel(dec) {
  const d = (dec || '').toLowerCase();
  if (isWaitDecision(d)) return d.includes('sin línea') || d.includes('sin linea') ? 'SIN LÍNEA' : 'ESPERAR';
  if (d.includes('early') || d.includes('temprano')) return 'TEMPRANO';
  if (d.includes('late')) return 'TARDÍO';
  if (d.includes('fuerte')) return d.includes('under') ? 'UNDER ★' : 'OVER ★';
  if (d.includes('under') || d.includes('over')) return 'VIGILAR';
  return 'ESPERAR';
}
function parseStake(stake) {
  const match = String(stake || '').match(/-?\d+(\.\d+)?/);
  return match ? parseFloat(match[0]) : 0;
}

function getClockMin(pick) {
  const c = pick.clock;
  if (c === null || c === undefined) return null;
  if (typeof c === 'number') return c;
  const s = String(c).trim().toUpperCase();
  if (['HT','BT','HALF','HALFTIME','BREAK','FT','FINAL','END',''].includes(s)) return null;
  if (s.includes(':')) {
    const [m, sec] = s.split(':');
    const mi = parseInt(m, 10), si = parseInt(sec, 10);
    if (Number.isFinite(mi) && Number.isFinite(si)) return mi + si / 60;
    return null;
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

export default function MobileCard({ pick, selected, onClick, tick, onLineChange, tgSent, tgLine, dataTour }) {
  const lineaRaw = pick.linea ?? pick.line ?? pick.over_under ?? null;
  // Persistir línea válida en localStorage (TTL 5 min)
  if (lineaRaw && parseFloat(lineaRaw) > 0 && pick.id) {
    saveLineToCache(pick.id, lineaRaw, pick.linea_source, pick.linea_ts);
  }
  const persistedCache = pick.id ? getLineFromCache(pick.id) : null;
  // Línea efectiva: línea actual o la cacheada
  const effectiveLinea = lineaRaw && parseFloat(lineaRaw) > 0 ? lineaRaw : (persistedCache?.linea ?? null);

  const [line, setLine] = useState(effectiveLinea ? String(effectiveLinea) : '');
  // Diferencia derivada DIRECTAMENTE de la prop. Antes estaba en useState y
  // se quedaba congelada al primer render. Mismo fix que GameRow.
  const edge = pick.edge ?? 0;
  const initial = (() => {
    const hasTrampas = typeof pick.trampas === 'number' && pick.trampas > 0;
    if (hasTrampas && parseStake(pick.stake) === 0) return { label: 'ESPERAR', cls: 'wait' };

    // MODO CONSERVADOR: solo FUERTE si Firebase dice "fuerte" explícitamente
    return { label: getVerdictLabel(pick.decision), cls: getVerdictClass(pick.decision) };
  })();
  const [verdict, setVerdict]           = useState(initial.label);
  const [verdictClass, setVerdictClass] = useState(initial.cls);

  const proj    = parseFloat(pick.proyeccion || 0);
  const isEarly = isEarlyGame(pick);
  const isNoCov = isNoCoverage(pick);

  // ── RED DE SEGURIDAD: bloqueo HT/BT ────────────────────────
  // Si el partido está en entretiempo o break, forzamos ESPERAR
  // independientemente del veredicto que venga de Firebase.
  const clockUpper = String(pick.clock || '').toUpperCase().trim();
  const isHalftime = ['HT','BT','HALF','HALFTIME','BREAK'].includes(clockUpper);

  const isBlocked = isEarly || isNoCov || isHalftime;
  const conf    = parseFloat(pick.confianza || 0);

  // FASE V7 — categoría calculada por el bot en /live_games (manda sobre lógica local).
  const v6Categoria = pick.v6?.categoria || pick.categoria_dashboard || null;
  const v6Razon     = pick.v6?.razon_humana || pick.razon_humana || null;

  // Si está bloqueado, forzar veredicto correspondiente
  const displayVerdict = isHalftime
    ? 'ESPERAR'
    : isNoCov
    ? 'SIN COBERTURA'
    : isEarly
    ? 'TEMPRANO'
    : verdict;
  const displayVerdictClass = isHalftime
    ? 'wait'
    : isNoCov
    ? 'wait'
    : isEarly
    ? 'early'
    : verdictClass;

  // Diseño neutro: edge en gris siempre, color sólo en pill veredicto.
  // DIFERENCIA = proyección − línea (cruda), igual que GameRow. El edge ajustado
  // (pick.edge) se mantiene para el veredicto; esto es solo display.
  const _lineNum   = parseFloat(effectiveLinea);
  const _crudaDiff = (proj > 0 && _lineNum > 0) ? (proj - _lineNum) : null;
  const edgeNum   = isBlocked ? 0 : (_crudaDiff || 0);
  const edgeStr   = isBlocked ? '—' : (_crudaDiff !== null && !isNaN(_crudaDiff) ? `${_crudaDiff >= 0 ? '+' : ''}${_crudaDiff.toFixed(1)}` : '—');

  // Lógica de visibilidad de proyección (dashboard v5)
  const periodNum   = Number(pick.period) || 0;
  const clockMinNum = getClockMin(pick);
  const isDescartado = (
    pick.v6?.categoria === 'DESCARTADO' ||
    pick.categoria_dashboard === 'DESCARTADO'
  );
  // 2026-05-21: proyección visible en TODOS los partidos desde el minuto 1 del Q1.
  // (antes oculta con pill "Tranqui, ya verás mi predicción"). Si aún no hay
  // proyección (proj<=0) se muestra "—".
  const showProjection = true;

  function getProjTooltip() {
    if (isDescartado) {
      return 'Esta liga yo como motor V6 la descarto, pero la proyección te la muestro como referencia';
    }
    if (periodNum === 4) {
      return 'Mi predicción en Q4 es más precisa, tengo toda la información que necesito';
    }
    return 'Estoy proyectando, pero mi predicción en Q4 es más precisa';
  }

  const tgLineNum = tgLine != null ? parseFloat(tgLine) : null;
  const hasTgLine = tgSent && Number.isFinite(tgLineNum) && tgLineNum > 0;

  // FIX: función recalc eliminada — el input manual fue removido.
  // El edge ahora siempre viene de Firebase (pick.edge) calculado por el engine Python.

  return (
    <div
      className={`mobile-card ${selected ? 'selected' : ''} ${isBlocked ? 'early' : ''} ${displayVerdict.includes('★') ? 'fuerte' : ''} ${!showProjection ? 'mc-prelim' : ''}`}
      data-tour={dataTour}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      {/* Header */}
      <div className="mc-top">
        <div>
          <div className="mc-match">{pick.partido || '—'}</div>
          <div className="mc-meta">
            {pick.period > 0 && <span className="live-dot" />}
            {esHandballPick(pick)
              ? periodLabel(pick)
              : `Q${pick.period || '?'}${pick.min_jugados ? ` · min ${getDisplayMinJugados(pick)}` : (pick.clock ? ` ${pick.clock}` : '')}`} · {pick.liga || ''}
            {pick.pace_source && (
              <span style={{
                marginLeft: 5,
                fontSize: 8,
                fontWeight: 700,
                padding: '1px 5px',
                borderRadius: 3,
                background: pick.pace_source === 'learned'
                  ? 'var(--pos-soft)'
                  : 'var(--warn-soft)',
                color: pick.pace_source === 'learned'
                  ? 'var(--pos)'
                  : 'var(--warn)',
              }}>
                {pick.pace_source === 'learned' ? 'CALC' : 'DEF'}
              </span>
            )}
          </div>
        </div>
        {(() => {
          // FASE V7 — categoría server-side prevalece sobre lógica local
          if (v6Categoria === 'ENTRAR') {
            const fbEdgeNum = parseFloat(pick.edge || 0);
            const sideRaw   = (pick.v6?.side || pick.side || '').toUpperCase();
            const side      = sideRaw === 'UNDER' || sideRaw === 'OVER'
              ? sideRaw
              : (fbEdgeNum < 0 ? 'UNDER' : 'OVER');
            return (
              <span
                className="vtag fuerte-over"
                title={v6Razon || undefined}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <span aria-hidden="true">⚡</span>
                <span>ENTRAR</span>
                <span style={{
                  fontSize: 8.5, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                  background: side === 'UNDER' ? 'var(--neg-soft)' : 'var(--pos-soft)',
                  color:      side === 'UNDER' ? 'var(--neg)'      : 'var(--pos)',
                }}>{side}</span>
              </span>
            );
          }
          if (v6Categoria === 'SOLO_INFO') {
            return <span className="vtag wait" title={v6Razon || undefined}>SOLO INFO</span>;
          }
          if (v6Categoria === 'DESCARTADO') {
            return <span className="vtag wait" title={v6Razon || undefined}>DESCARTADO</span>;
          }
          if (v6Categoria === 'VIGILAR' || v6Categoria === 'ESPERAR' || v6Categoria === 'TEMPRANO') {
            const cls = v6Categoria === 'VIGILAR' ? 'over' : v6Categoria === 'TEMPRANO' ? 'early' : 'wait';
            return <span className={`vtag ${cls}`} title={v6Razon || undefined}>{v6Categoria}</span>;
          }
          // Fallback legacy
          if (isBlocked) {
            return (
              <span className="vtag verdict-blocked" title="Esperando datos para veredicto eficiente">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 3, flexShrink: 0 }}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                BLOQUEADO
              </span>
            );
          }
          return <span className={`vtag ${displayVerdictClass}`}>{displayVerdict}</span>;
        })()}
      </div>

      {/* Grid 2x2: Marcador / Línea / Proyección / Diferencia */}
      <div className="mc-grid mc-grid-2col">
        <div>
          <div className="mc-stat-label">MARCADOR</div>
          <div className="mc-stat-val">
            {(() => {
              if (!pick.score) return '—';
              const [aS, hS] = pick.score.split('-').map(s => s.trim());
              return (
                <>
                  <span className="team-abbr team-abbr-away">{pick.away_team || '?'}</span>
                  {' '}{aS}{' '}
                  <span className="sep">–</span>
                  {' '}{hS}{' '}
                  <span className="team-abbr team-abbr-home">{pick.home_team || '?'}</span>
                </>
              );
            })()}
          </div>
        </div>
        <div className={hasTgLine ? 'mc-line-cell mc-line-cell--tg' : 'mc-line-cell'}>
          <div className="mc-stat-label">LÍNEA O/U</div>
          <div className="mc-stat-val">
            {effectiveLinea && parseFloat(effectiveLinea) > 0 ? effectiveLinea : '—'}
          </div>
          {hasTgLine && (
            <span className="line-tg-sub">📩 TG enviada en {tgLineNum}</span>
          )}
        </div>
      </div>

      {showProjection ? (() => {
        // EDGE en lugar de proyección — pick.edge (ajustado por confidence
        // del motor Python). Color verde si OVER (>0), azul si UNDER (<0).
        const edgeNum = parseFloat(pick.edge);
        const hasEdge = pick.edge != null && !isNaN(edgeNum);
        const color = !hasEdge ? 'var(--muted-2)'
                    : edgeNum > 0 ? 'var(--pos)'
                    : edgeNum < 0 ? 'var(--info)'
                    : 'var(--muted)';
        const sign = hasEdge && edgeNum >= 0 ? '+' : '';
        return (
          <div style={{ marginBottom: 12 }}>
            <div className="mc-stat-label">EDGE</div>
            <div className="mc-stat-val" style={{ color, fontVariantNumeric: 'tabular-nums' }}>
              {hasEdge ? `${sign}${edgeNum.toFixed(1)}` : '—'}
              {hasEdge && <Tooltip text={getProjTooltip()} />}
            </div>
          </div>
        );
      })() : (
        <div className="mc-prelim-banner">
          <span aria-hidden="true">👀</span>
          <span>Tranqui, ya verás mi predicción</span>
          <span className="mc-prelim-sub">El motor mostrará el edge cuando tenga datos confiables</span>
        </div>
      )}

      {/* Footer: estado de línea (izq) + badge TG (der) */}
      <div className="mc-foot" onClick={e => e.stopPropagation()}>
        <LineStatusBadge
          lineSource={pick.linea_source ?? pick.line_source}
          lineTs={pick.linea_ts ?? pick.line_ts}
        />
        {tgSent ? (
          <span className="tg-badge" title="Alerta enviada al canal VIP de Telegram">
            ✈ TG
          </span>
        ) : (
          <span style={{ fontSize: 10, color: 'var(--muted-2)' }}>
            {pick.timestamp
              ? new Date(pick.timestamp).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
              : ''}
          </span>
        )}
      </div>
    </div>
  );
}
